/**
 * useReportForm - Form, validation, and image state for report submission
 *
 * Extracted from useReportSubmission.js. Owns:
 * - Form fields (description, email, anonymity/urgent/test flags, tag chips)
 * - Image picking/upload progress state
 * - Validation state (description required)
 *
 * The parent hook owns the organization dropdown, so the selected value is
 * injected only to clear tag chips when it changes.
 *
 * @param {string} OrgFilterValue - Currently selected organization
 */
import { useState, useEffect, useCallback } from 'react';

import { t } from '../i18n';
import { validateRequired } from '../util/validation';

export default function useReportForm(OrgFilterValue) {
  // Form state
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [isUrgent, setIsUrgent] = useState(false);
  const [isTest, setIsTest] = useState(false);
  const [KPIS, setKPIS] = useState([]);
  const [Locations, setLocations] = useState([]);

  // Image state
  const [imagesToSend, setImagesToSend] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStage, setUploadStage] = useState('Picking');
  const [imageLoading, setImageLoading] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);

  /**
   * Validate form
   */
  const validateForm = useCallback(() => {
    const formErrors = {};
    if (!description.trim()) {
      formErrors.description = validateRequired('', t('support:descriptionField'));
    }
    setErrors(formErrors);
    setIsFormValid(Object.keys(formErrors).length === 0);
  }, [description]);

  /**
   * Remove an image from the list
   */
  const removeImage = useCallback((imageToRemove) => {
    setImagesToSend((prev) => prev.filter((image) => image !== imageToRemove));
  }, []);

  /**
   * Reset form after successful submission
   */
  const resetForm = useCallback(() => {
    setDisplayErrors(false);
    setKPIS([]);
    setLocations([]);
    setDescription('');
    setImagesToSend([]);
    setUploadedImages([]);
    setUploadStage('Picking');
    setUploadProgress({});
  }, []);

  // Validate on description change
  useEffect(() => {
    validateForm();
  }, [description, validateForm]);

  // Update upload progress when images change
  useEffect(() => {
    if (!imagesToSend.length) return;

    const uploadProg = {};
    imagesToSend.forEach((value) => {
      uploadProg[value] = 'Picked';
    });
    setUploadProgress(uploadProg);
  }, [imagesToSend]);

  // Clear chips when organization changes
  useEffect(() => {
    setKPIS([]);
    setLocations([]);
  }, [OrgFilterValue]);

  return {
    // Form state
    description,
    setDescription,
    email,
    setEmail,
    anonymous,
    setAnonymous,
    isUrgent,
    setIsUrgent,
    isTest,
    setIsTest,
    KPIS,
    setKPIS,
    Locations,
    setLocations,

    // Image state
    imagesToSend,
    setImagesToSend,
    uploadedImages,
    setUploadedImages,
    uploadProgress,
    setUploadProgress,
    uploadStage,
    setUploadStage,
    imageLoading,
    setImageLoading,
    removeImage,

    // Validation state
    errors,
    isFormValid,
    displayErrors,
    setDisplayErrors,
    resetForm,
  };
}
